import Head from 'next/head';
import Link from 'next/link';
import React, { useContext } from 'react';
import { ContactContext, FrontLayout } from '~/layouts/FrontLayout';

import { Button, Form, Input } from '../components/form';
import { Block, Col, Container, FlexRow, Row } from '../components/layout';
import { Nav } from '../components/nav';
import { Heading, Text } from '../components/type';
import { Avatar, Divider, Image, Shape, Scrollable } from '../components/ui';
import { team } from '~/data/team';

function Company() {
  const currentYear = new Date().getFullYear();
  const contactFormContext = useContext(ContactContext);

  return (
    <>
      <Head>
        <title>WelcomeLend</title>
        <meta
          name="description"
          content="WelcomeLend is a Commercial Real Estate platform that makes it easy to collect and review documents, build an OM, and connect sponsors and lenders all in one secure place."
        />
      </Head>
      <Block as="section" utils={{ pt: 14 }} desktop={{ pt: 16 }}>
        <Container>
          <Row utils={{ justifyContent: 'center' }}>
            <Col span="100%" desktop={{ span: '63%' }}>
              <Heading as="h6" utils={{ fontSize: 'xs', textAlign: 'center', textTransform: 'uppercase', color: 'primary', mb: 6 }}>
                Our Mission
              </Heading>
              <Heading as="h1" utils={{ textAlign: 'center', fontSize: '8xl', fontWeight: 'bolder', mb: 6 }}>
                We’re on a mission to simplify CRE transactions
              </Heading>
              <Text utils={{ textAlign: 'center', fontSize: '2xl', color: 'gray800', mb: 8 }} desktop={{ mb: 13 }}>
                WelcomeLend is a platform that brings the right people, information, and tools together to close deals.
              </Text>
            </Col>
          </Row>
          <Row gutter={0}>
            <Col span="35%">
              <Image utils={{ position: 'relative', borderRadius: 'base', boxShadow: 6, mt: 8 }} isFluid src="/img/photos/photo-1.jpg" alt="..." />
            </Col>
            <Col span="40%" style={{ marginLeft: '-15%' }}>
              <Image utils={{ borderRadius: 'base', boxShadow: 6, mt: 10 }} desktop={{ mt: 13 }} isFluid src="/img/photos/photo-2.jpg" alt="..." />
            </Col>
            <Col span="25%" style={{ marginLeft: '-15%' }}>
              <Image utils={{ position: 'relative', borderRadius: 'base', boxShadow: 6 }} isFluid src="/img/photos/photo-3.jpg" alt="..." />
            </Col>
            <Col span="34.999%" style={{ marginLeft: '-5%' }}>
              <Image utils={{ borderRadius: 'base', boxShadow: 6, mt: 11 }} desktop={{ mt: 15 }} isFluid src="/img/photos/photo-4.jpg" alt="..." />
            </Col>
          </Row>
        </Container>
      </Block>
      <Block as="section" utils={{ pt: 10 }} desktop={{ pt: 14 }}>
        <Container>
          <Row utils={{ justifyContent: 'center' }}>
            <Col span="100%" desktop={{ span: '80%' }}>
              <Text utils={{ fontSize: 'lg', mb: 6 }}>
                We started WelcomeLend to solve a problem: Commercial Real Estate (CRE) financing is inefficient and fragmented. Borrowers consistently fail to
                find the right lending source for their deals. Deals that should be funded die due to avoidable causes. Borrowers and brokers lack the proper
                tools to effectively structure, present, and place deals. Lenders are inundated with deals that are unorganized and often don't match their
                program criteria.
              </Text>
              <Text utils={{ fontSize: 'lg', mb: 6 }}>
                As a team of seasoned CRE brokers, we felt these pains firsthand and couldn't find the right solution. It became clear to us there was an
                opportunity to improve CRE transactions for everyone. We decided to scratch our own itch and build software for the borrowers we represent and
                ourselves.
              </Text>
              <Text utils={{ fontSize: 'lg', mb: 6 }}>
                Since then, WelcomeLend has become a highly sophisticated and automated cloud-based platform that powers every facet of our operation. It tracks
                tens of thousands of active CRE deals, manages every deal we touch from initial contact through funding, and uses a proven matching algorithm to
                compare each deal point against our lender database to find the ideal lending source and structure.{' '}
              </Text>
              <Text utils={{ fontSize: 'lg', mb: 6 }}>
                Since launching, we've closed over $1B in transactions. Despite extremely challenging markets in 2022, we closed over $440MM. We continue to
                ruthlessly attack inefficiencies and build the best platform for closing commercial real estate deals.
              </Text>
              <Text utils={{ fontSize: 'lg', mb: 6 }}>We believe WelcomeLend will fundamentally change CRE financing....and we're just getting started.</Text>
              <Text utils={{ mt: 10, mb: 6 }}>
                <Image src="/img/signatures/david-schwartz.png" isFluid style={{ width: '50%', maxWidth: '150px' }} />
                <Image src="/img/signatures/david-gamache.png" isFluid style={{ width: '50%', maxWidth: '200px' }} />
              </Text>
              <Text utils={{ color: 'gray700' }}>David Schwartz & David Gamache</Text>
            </Col>
          </Row>
          <Row utils={{ justifyContent: 'center' }}>
            <Col span="100%" desktop={{ span: '80%' }}>
              <Divider utils={{ mt: 10, mb: 0 }} desktop={{ mt: 14 }} />
            </Col>
          </Row>
        </Container>
      </Block>
      <Block as="section" utils={{ py: 10 }} desktop={{ pb: 16, pt: 14 }}>
        <Container>
          <Row utils={{ justifyContent: 'center' }}>
            <Col span="100%" desktop={{ span: '80%' }}>
              <Row>
                <Col span="100%" desktop={{ span: '50%' }}>
                  <Heading as="h2" utils={{ fontSize: '8xl', fontWeight: 'bolder', mb: 6 }}>
                    Our Team
                  </Heading>
                  <Text utils={{ fontSize: '2xl', color: 'gray800', mb: 9 }} desktop={{ mb: 12 }}>
                    We are a U.S. based team with talented tech employees from around the globe.
                  </Text>
                </Col>
              </Row>
              <Row>
                {team.map((member, inx) => (
                  <Col span="100%" desktop={{ span: '33.33%' }} key={inx}>
                    <Block utils={{ mb: 10 }} desktop={{ mb: 12 }}>
                      <Block as="div" utils={{ textAlign: 'center' }} desktop={{ textAlign: 'left' }}>
                        <Avatar size="2xl" src={member.image} alt={member.name} utils={{ mb: 5 }} />
                        <Heading as="h6" utils={{ fontSize: 'lg' }}>
                          {member.name}
                        </Heading>
                        <Text utils={{ color: 'gray700', mb: 3 }}>{member.role}</Text>
                      </Block>
                      <Text utils={{ fontSize: 'base', mb: 6 }}>{member.description}</Text>
                    </Block>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </Block>
      <Block utils={{ position: 'relative' }}>
        <Shape slant={8} desktop={{ slant: 15 }} />
      </Block>
      <Block as="section" utils={{ py: 11, bgColor: 'black' }} desktop={{ pt: 16, pb: 18 }}>
        <Container>
          <Row utils={{ justifyContent: 'center' }}>
            <Col span="100%" desktop={{ span: '65%' }}>
              <Heading as="h6" utils={{ fontSize: 'xs', textAlign: 'center', textTransform: 'uppercase', color: 'primary', mb: 6 }}>
                Give us a try
              </Heading>
              <Heading as="h1" utils={{ textAlign: 'center', color: 'white', fontSize: '7xl', fontWeight: 'bolder', mb: 6 }}>
                WelcomeLend makes financing your deal easy.
              </Heading>
              <Text utils={{ textAlign: 'center', fontSize: '2xl', color: 'gray800', mb: 9 }}>
                Our capital markets team is 100% focused on finding you the perfect funding source and structure for your commercial real estate deal.
              </Text>
              <Form
                utils={{ maxWidth: '420px', mx: 'auto', mb: 0 }}
                onSubmit={(e) => {
                  e.preventDefault();

                  const email = contactFormContext.contactForm.email;
                  if (email.length !== 0) {
                    contactFormContext.setIsContactModalOpen(true);
                    (e.target as HTMLFormElement).reset();
                  }
                }}
              >
                <FlexRow>
                  <Input
                    utils={{ bgColor: 'white10', borderColor: 'transparent', boxShadow: 'none', color: 'white', mr: 6 }}
                    type="email"
                    placeholder="Email"
                    name="email"
                    required
                    onChange={(e) => {
                      const email = e.target.value;
                      contactFormContext.setContactFormEmail(email);
                    }}
                  />
                  <Button>Get Started</Button>
                </FlexRow>
              </Form>
            </Col>
          </Row>
        </Container>
      </Block>
      <Block as="footer" utils={{ bgColor: 'black' }}>
        <Container>
          <Row>
            <Col>
              <Divider utils={{ my: 0, borderColor: 'white10' }} />
            </Col>
          </Row>
          <Row utils={{ alignItems: 'center', py: 8 }}>
            <Col span="100%" desktop={{ span: 'auto' }}>
              <FlexRow utils={{ alignItems: 'center', justifyContent: 'center' }}>
                <Image src="/img/brand/brand.svg" style={{ width: 32, height: 'auto' }} />
                <Heading utils={{ fontSize: 'base', color: 'white', ml: 4 }} mobile={{ display: 'none' }}>
                  WelcomeLend
                </Heading>
              </FlexRow>
            </Col>
            <Col span="100%" utils={{ mt: 8, mb: 7 }} desktop={{ span: true, mt: 0, mb: 0 }}>
              <Scrollable utils={{ justifyContent: 'center' }}>
                <Nav>
                  <Link href="/" passHref legacyBehavior>
                    <Text as="a" utils={{ color: 'gray700', mx: 4, whiteSpace: 'nowrap' }}>
                      Product
                    </Text>
                  </Link>
                  <Link href="/company" passHref legacyBehavior>
                    <Text as="a" utils={{ color: 'gray700', mx: 4, whiteSpace: 'nowrap' }}>
                      Company
                    </Text>
                  </Link>
                  <Link href="/closed-deals" passHref legacyBehavior>
                    <Text as="a" utils={{ color: 'gray700', mx: 4, whiteSpace: 'nowrap' }}>
                      Closed Deals
                    </Text>
                  </Link>
                  <Link href="/" passHref scroll={false} legacyBehavior>
                    <Text as="a" utils={{ color: 'gray700', mx: 4, whiteSpace: 'nowrap' }} onClick={() => contactFormContext.setIsContactModalOpen(true)}>
                      Contact
                    </Text>
                  </Link>
                </Nav>
              </Scrollable>
            </Col>
            <Col span="100%" utils={{ textAlign: 'center' }} desktop={{ span: 'auto' }}>
              <Text as="span" utils={{ fontSize: 'sm', color: 'gray700' }}>
                Copyright {currentYear}
              </Text>
            </Col>
          </Row>
        </Container>
      </Block>
    </>
  );
}

Company.Layout = FrontLayout;

export default Company;
